import State from "../state/state.ts";
import V1GroupJoined from "../payloads/incoming/v1-group-joined.ts";
import LogUtils from "../utils/log-utils.ts";
import MemberState from "../enums/member-state.ts";
import V1GroupReadied from "../payloads/incoming/v1-group-readied.ts";
import GroupState from "../enums/group-state.ts";
import V1UserGuestLoggedIn from "../payloads/incoming/v1-user-guest-logged-in.ts";
import Route from "../enums/route.ts";
import V1GroupRestaurantCategoriesChosen from "../payloads/incoming/v1-group-restaurant-categories-chosen.ts";
import V1GroupRestaurantsChosen from "../payloads/incoming/v1-group-restaurants-chosen.ts";

/**
 * This class is for API logic meant to be shared between many UI screens, as there tends to be a lot
 * of common functionality that occurs for each Websocket API payload.
 *
 * Screen-specific functionality should still exist within each distinct screen,
 * but all shared logic should be extracted and shared via this class.
 */
export default class SharedApiLogic {
  public static handleV1UserGuestLoggedIn(
    debug: boolean,
    state: State,
    navigateCallback: (route: Route) => void,
    v1UserGuestLoggedIn: V1UserGuestLoggedIn,
  ): void {
    LogUtils.debug(debug, "Successfully logged in as a guest user.");

    // save the user account's ID + name
    state.userState.id = v1UserGuestLoggedIn.payload.user.id;
    state.userState.name = v1UserGuestLoggedIn.payload.user.name;

    // now that we're logged in,
    // check to see if we should auto-redirect to "Join Group" screen
    try {
      const urlSearchParams: URLSearchParams = (new URL(document.location.toString())).searchParams;
      if (urlSearchParams.has("groupcode")) {
        navigateCallback(Route.JOIN_GROUP);
        return;
      }
    } catch (error) {
      LogUtils.error(`Failed to check query parameters for Group Code: ${error}`);
    }

    // if no group code in query parameters, default to Main Menu
    navigateCallback(Route.MAIN_MENU);
  }

  public static handleV1GroupJoined(state: State, v1GroupJoined: V1GroupJoined): void {
    // make sure the group actually belongs to the logged-in user
    if (v1GroupJoined.payload.group.ownerId !== state.userState.id) {
      LogUtils.error(
        "received V1GroupJoined payload, but the logged-in user isn't the group owner",
      );
    }

    // make sure the logged-in user isn't somehow also the "new" user that just joined the group (should be impossible)
    if (v1GroupJoined.payload.user.id === state.userState.id) {
      LogUtils.error(
        "received V1GroupJoined payload, but the logged-in user is both the group owner AND the 'new' user that joined the group",
      );
    }

    // add member to the group
    state.groupState.addMemberToGroup(
      v1GroupJoined.payload.group.id,
      v1GroupJoined.payload.user.id,
      MemberState.JOINED,
    );

    // let everyone know a new user just joined the group!
    LogUtils.info(`${v1GroupJoined.payload.user.name} joined!`);
  }

  public static handleV1GroupReadied(state: State, v1GroupReadied: V1GroupReadied): void {
    // make sure event is related to this client's current group
    if (v1GroupReadied.payload.group_id !== state.groupState.currentGroupId) {
      LogUtils.error(
        `received V1GroupReadied payload for wrong group ID: '${v1GroupReadied.payload.group_id}' 
          (current group ID: '${state.groupState.currentGroupId}')`,
      );
    }

    // update the group's state
    state.groupState.updateGroupState(
      v1GroupReadied.payload.group_id,
      GroupState.READIED_UP,
    );
  }

  public static handleV1GroupRestaurantCategoriesChosenCallback(
    debug: boolean,
    state: State,
    v1GroupRestaurantCategoriesChosen: V1GroupRestaurantCategoriesChosen,
  ): void {
    LogUtils.debug(debug, "A user submitted their Restaurant Category Preferences.");

    // make sure payload belongs to the current group
    if (v1GroupRestaurantCategoriesChosen.payload.group_id !== state.groupState.currentGroupId) {
      LogUtils.error("received V1GroupRestaurantCategoriesChosen, but it doesn't belong to the current group");
    }

    // update the group member's state
    state.groupState.updateMemberState(
      v1GroupRestaurantCategoriesChosen.payload.group_id,
      v1GroupRestaurantCategoriesChosen.payload.user_id,
      MemberState.SUBMITTED_RESTAURANT_CATEGORY_PREFERENCES,
    );
  }

  public static handleV1GroupRestaurantsChosenCallback(
    debug: boolean,
    state: State,
    v1GroupRestaurantsChosen: V1GroupRestaurantsChosen,
  ): void {
    LogUtils.debug(debug, "A user submitted their Restaurant Preferences.");

    // make sure payload belongs to the current group
    if (v1GroupRestaurantsChosen.payload.group_id !== state.groupState.currentGroupId) {
      LogUtils.error(
        "received V1GroupRestaurantsChosen on the restaurantCategories screen, but it doesn't belong to the current group",
      );
    }

    // update the group member's state
    state.groupState.updateMemberState(
      v1GroupRestaurantsChosen.payload.group_id,
      v1GroupRestaurantsChosen.payload.user_id,
      MemberState.SUBMITTED_RESTAURANT_PREFERENCES,
    );
  }
}
